<script setup lang="ts">
import type { Track } from "~/types/audio"
import { getFullscreenLink } from "~/utilities/urls"

const props = defineProps<{ track: Track }>()

const productVariantsStore = useProductVariantsStore()

const personalizationStore = usePersonalizationStore()

const statusIcon = computed(() => {
  const isArticleRead = personalizationStore.readHistory.some(
    readHistoryTrack => readHistoryTrack.id === props.track.id,
  )

  return isArticleRead ? "circle-filled" : "circle-hollow"
})

const { currentTrack, tts } = storeToRefs(useAudioStore())

const isLoading = computed(() => tts.value?.status === "loading")

const to = computed(() => getFullscreenLink(props.track.frontendHref, true))

const ref = useTemplateRef("container")

const isCurrent = computed(() => !!currentTrack.value && props.track.id === currentTrack.value?.id)

watch([ref, isCurrent], ([container, isCurrent], [oldContainer]) => {
  if (container && isCurrent) {
    container.scrollIntoView({
      // On mount (i.e., when oldContainer is undefined), we want to scroll instantly
      behavior: oldContainer ? "smooth" : "instant",
      block: "center",
      inline: "center",
    })
  }
})
</script>

<template>
  <li
    :id="String(track.id)"
    ref="container"
    class="font-national2 mb-6 flex w-full !cursor-pointer justify-between gap-2 border-b-[2px] border-solid border-[--theme-translucent-light] pb-4 text-label-md text-lightest hover:brightness-[85%]"
    :class="{ '!font-bold !text-lighter': isCurrent }"
  >
    <NuxtLink v-if="to" :to="to">
      <h3 class="line-clamp-2 hover:text[--theme-lighter]" v-text="track.title" />
    </NuxtLink>
    <h3 v-else class="line-clamp-2" v-text="track.title" />
    <div class="flex items-center gap-4">
      <p v-if="isCurrent" class="hidden md:block">
        <span :class="{ 'animate-color-pulse': isLoading }">
          {{ isLoading ? 'Laster lyd...' : 'Lytter' }}<span class="sr-only"> til denne artikkelen</span>
        </span>
      </p>

      <UIcon
        v-else-if="productVariantsStore.activeVariant?.userHasLicence"
        :name="statusIcon"
        size="md"
      />
    </div>
  </li>
</template>
